import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Analytics from 'analytics-opencare';
import { postFeedback } from 'connect/actions';
import { Alert, Form, QuestionCircleFilled, Tooltip } from 'opencare-crown';
import { useAnalyticsService } from 'services/AnalyticsService';

import FloatingInput from '../../../../../common/components/FloatingInput/FloatingInput';
import {
  AdditionalBlock,
  PrimaryButton,
  Subtitle,
  Title,
} from '../../../../../common/styled-components';
import { useFunnelStorage } from '../../../../storage/Storage';
import { ButtonBlock, StepWrapper } from '../../../../styled-components';
import { generateStepAnalytics, STEP_KEY } from '../../../../utils';

const analytics = new Analytics();

const NotEligible = ({ position }) => {
  const dispatch = useDispatch();
  const storage = useFunnelStorage();

  const analyticsService = useAnalyticsService();

  const [notEligibleForm] = Form.useForm();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const savedInsuranceProgress = storage.get(STEP_KEY.INSURANCE);
  const hasInsurance = get(savedInsuranceProgress, 'hasInsurance') === 'Yes';

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.FUNNEL_LEAD,
      pageName: 'funnel_ineligible',
      ...generateStepAnalytics(position, storage),
      ...analyticsService.analyticsData,
    });
  }, []);

  const handleSubmit = values => {
    analytics.trackForm({
      pageType: analytics.pageTypes.FUNNEL_LEAD,
      pageName: 'funnel_ineligible',
      name: 'wizard',
      section: 'not-eligible',
      ...generateStepAnalytics(position, storage, {
        response: values.email,
      }),
    });

    dispatch(
      postFeedback('UNINSURED_PATIENT', {
        UNINSURED_PATIENT: { email: values.email, planId: 'Uninsured' },
      }),
    );
    storage.clearCache();
    setFormSubmitted(true);
  };

  return (
    <div data-testid="substep-not-eligible">
      <Title data-testid="title">
        {!hasInsurance
          ? (
            <>
              Unfortunately, our network doesn't cover payment from social
              assistance programs right now.
            </>
          )
          : (
            <>
              Unfortunately, we only specialize in serving patients with PPO
              insurance plans{' '}
              <Tooltip
                title={
                  'PPO plans, or "Preferred Provider Organization" plans, are one of the most popular types of dental insurance plans. They allow subscribers to choose from and visit the insurance company’s network of preferred doctors.'
                }
              >
                <QuestionCircleFilled />
              </Tooltip>
              right now.
            </>
          )
        }
      </Title>
      <Subtitle>
        Leave us your email and we'll notify you when our network expands.
      </Subtitle>
      <StepWrapper>
        <Form form={notEligibleForm} onFinish={handleSubmit}>
          <AdditionalBlock>
            <FloatingInput
              field="email"
              form={notEligibleForm}
              label="Enter your email address"
              required
              message="Required. Please enter a valid email"
              testId="email"
              capitalize={false}
              type="email"
            />
            {formSubmitted
              && (
                <Alert
                  data-testid="form-submitted"
                  type="success"
                  message="Thank you! We'll notify you when our network expands."
                />
              )
            }
          </AdditionalBlock>
          <ButtonBlock>
            <PrimaryButton type="submit" data-testid="submit">
              Get notified
            </PrimaryButton>
          </ButtonBlock>
        </Form>
      </StepWrapper>
    </div>
  );
};

NotEligible.defaultProps = {
  position: '',
};

NotEligible.propTypes = {
  position: PropTypes.string,
};

export default NotEligible;
